import React, {useContext, useMemo} from 'react';
import dayjs from "dayjs";
import {Sorter} from "../../shared/components/Sorter";
import LogTemplate from "./LogTemplate";
import {AuthContext} from "../../shared/contexts/authContext";
import {getItemName, getItemsNameAsNoneType, getPayName} from "../../shared/utils/basic";
import GoToUserInfoButton from "../../shared/components/buttons/GoToUserInfoButton";
import {useItemKrTextData} from "../probability/useItemData";
import IsLoading from "../../shared/components/IsLoading";
import ErrorPage from "../../shared/components/ErrorPage";
import {getGachaLogColumns} from "./columns/gachaLogColumns";

const OperationLog = ({type}) => {
    const {currentUser} = useContext(AuthContext);

    const {data: krItemTextData, isLoading, error} = useItemKrTextData();

    const defaultSearchParams = useMemo(() => ({
        begin: dayjs().subtract(7, 'day').startOf('day').unix(),
        end: dayjs().endOf('day').unix(),
        type: '',
        value: '',
        logType: type,
        isUnix: true
    }), [type]);

    // 공통 컬럼
    const pidColumn = {
        title: 'PID',
        dataIndex: 'pid',
        search: true,
    };
    const nicknameColumn = {
        title: '닉네임',
        dataIndex: 'nickname',
        search: true,
    };
    const dateColumn = {
        title: '날짜',
        dataIndex: 'created',
        render: (time) => dayjs.unix(time).format('YYYY-MM-DD HH:mm:ss'),
        sorter: (a, b) => Sorter.DATE(a.time, b.time),
    };
    const userInfoColumn = {
        title: '유저 정보',
        dataIndex: 'pid',
        render: (pid) => <GoToUserInfoButton pid={pid}/>,
    };

    // 각 로그 타입별 컬럼 정의
    const shopColumns = [
        pidColumn,
        nicknameColumn,
        {
            title: '지불 타입',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return getPayName(krItemTextData[currentUser.target], record.parsedLogData.payType);
            }
        },
        {
            title: '지불 금액',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.payCost;
            }
        },
        {
            title: '구매 아이템',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return getItemName(krItemTextData[currentUser.target], record.parsedLogData.itemId, record.parsedLogData.itemType);
            }
        },
        {
            title: '구매 개수',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.itemQuantity;
            }
        },
        dateColumn,
        userInfoColumn
    ];
    const gachaColumns = [
        pidColumn,
        nicknameColumn,
        ...getGachaLogColumns(krItemTextData, currentUser),
        dateColumn,
        userInfoColumn
    ];
    const passPackageColumns = [
        pidColumn,
        nicknameColumn,
        {
            title: '로그 타입',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return getPayName(krItemTextData[currentUser.target], record.parsedLogData.logType)
            }
        },
        {
            title: 'passPackageId',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.passPackageId;
            }
        },
        {
            title: 'passPackageStep',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.passPackageStep;
            }
        },
        dateColumn,
        userInfoColumn
    ];
    const eventColumns = [
        pidColumn,
        nicknameColumn,
        {
            title: '데이터 준비 중',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.passPackageId;
            }
        },
        {
            title: '데이터 준비 중',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.passPackageStep;
            }
        },
        {
            title: '데이터 준비 중',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return getPayName(krItemTextData[currentUser.target], record.parsedLogData.logType)
            }
        },
        dateColumn,
        userInfoColumn
    ];
    const changeBadgeColumns = [
        pidColumn,
        nicknameColumn,
        {
            title: '배지 시즌',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.badgeEventIndex;
            }
        },
        {
            title: '소모 배지IDX',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }

                const badgeIndexs = record.parsedLogData?.badgeIndexs ?? [];
                const badgeCounts = record.parsedLogData?.badgeCounts ?? [];
                return badgeIndexs.map((bIdx, index) => {
                    return getItemsNameAsNoneType(krItemTextData[currentUser.target], bIdx) + `(${bIdx}) ${badgeCounts[index]}개`;
                }).join(', ');
            }
        },
        {
            title: '상점 번호',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {}; // 오류 발생 시 기본값 설정
                    }
                }
                return record.parsedLogData.shopProductIndex;
            }
        },
        dateColumn,
        userInfoColumn
    ];
    const badgeRewardColumns = [
        pidColumn,
        nicknameColumn,
        {
            title: '배지 시즌',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.badgeEventIndex;
            }
        },
        {
            title: '획득 상품',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.shopProductIndex;
            }
        },
        {
            title: '교환 보상IDX',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                const shopItemIndexs = record.parsedLogData.shopItemIndexs || [];
                const shopItemTypes = record.parsedLogData.shopItemTypes || [];
                const shopItemQuantities = record.parsedLogData.shopItemQuantities || [];
                return shopItemIndexs.map((sIdx, index) => {
                    return getItemName(krItemTextData[currentUser.target], sIdx, shopItemTypes[index]) + `(${sIdx}) ${shopItemQuantities[index]}개`;
                }).join(', ');
            }
        },
        dateColumn,
        userInfoColumn
    ];
    const mergeEventRoundClearLogColumns = [
        pidColumn,
        nicknameColumn,
        {
            title: '이벤트 Type',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.logType;
            },
        },
        {
            title: '이벤트 ID',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.logValue;
            }
        },
        {
            title: '라운드 ID',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                return record.parsedLogData.roundId;
            },
        },
        {
            title: '획득 아이템',
            dataIndex: 'logString',
            render: (logString, record) => {
                if (!record.parsedLogData) {
                    try {
                        record.parsedLogData = JSON.parse(logString);
                    } catch (error) {
                        console.error('logString 파싱 오류:', error);
                        record.parsedLogData = {};
                    }
                }
                console.log(record.parsedLogData);
                return record.parsedLogData.itemId.map((item, index) => {
                    return getItemName(krItemTextData[currentUser.target], item, record.parsedLogData.itemType[index]) + `(${item}) ${record.parsedLogData.itemQuantity[index]}개`;
                }).join(', ');
            }
        },
        dateColumn,
        userInfoColumn
    ];
    // 로그 타입별 컬럼 매핑
    const columnsMap = {
        'RQGachaLog': gachaColumns,
        'RQShopLog': shopColumns,
        'RQPassPackageLog': passPackageColumns,
        'RQEventLog': eventColumns,
        'RQChangeBadgeLog': changeBadgeColumns,
        'RQBadgeRewardLog': badgeRewardColumns,
        'RQMergeEventRoundClearLog': mergeEventRoundClearLogColumns,
        'RQLevelUpLog': [],
        'RQMainMissionLog': [],
        'RQMergeMissionLog': [],
        'RQMissionLog': [],
        'RQStoreLayoutLog': [],
        'RQBookLog': [],
        'RQMatchLog': [],
    }

    if (isLoading) return <IsLoading/>;
    if (error) return <ErrorPage error={error}/>;
    return (
        <LogTemplate
            columns={columnsMap[type]}
            defaultSearchParams={defaultSearchParams}
            routerUrl={'/logs/operation'}
            logName={type.replace('RQ', '')}
        />
    );
};

export default OperationLog;